import React from 'react';
import { bool, func, string } from 'prop-types';
import classNames from 'classnames';

import CarModelPicker from '../../../components/CarModelPicker/CarModelPicker';
import { RegNumInput } from '../../WP/RegNumInput';
import { formatReg, normalizeReg } from '../../../util/savedCar';

import css from './CarBar.module.css';

/**
 * "Byt bil" — choose the car, with or without a registration number
 * (P25009-491 BR-6). One panel, the same content on both: a dropdown under
 * the button on a computer, a "Välj bil" bottom sheet on a phone (CSS decides).
 *
 *   1. Sök med registreringsnummer — the plate input + Kolla (RegNumInput,
 *      its "Välj motor" window included)
 *   2. Sök på din bilmodell — Bilmärke, Modell, Modellversion + Visa, from the
 *      car database (CarModelPicker); the car then has no plate
 *   3. Tidigare sökningar — the buyer's saved cars first (signed in), then
 *      this browser's recent searches, plates and models alike. The car on
 *      screen is highlighted; a recent search can be removed, and "Rensa
 *      historik" removes them all. Saved cars are managed under Dina bilar
 *      and are not removed from here.
 *
 * The panel stays MOUNTED while closed (only hidden). RegNumInput finishes
 * its own "Bekräfta" handler after the car has reached Redux, and its window
 * is a portal it owns; unmounting it at that moment tore the window down
 * under it (P25009-482, WMM191).
 */
const CarSwitchPanel = props => {
  const {
    isOpen,
    onClose,
    chips,
    activeKey,
    pickingKey,
    onPickChip,
    onRemoveChip,
    onClearHistory,
    onPlateCar,
    onModelCar,
    onSearchWithoutCar,
    hasCar,
    seriesHint,
    regNum,
    onRegNumChange,
  } = props;

  return (
    <div
      className={classNames(css.panelLayer, { [css.panelLayerOpen]: isOpen })}
      onClick={e => {
        // The phone sheet's backdrop closes it; nothing inside the sheet does.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={css.panel} role="dialog" aria-label="Välj bil" aria-hidden={!isOpen}>
        <div className={css.sheetHandle} aria-hidden="true" />
        <div className={css.sheetHeader}>
          <span className={css.sheetTitle}>Välj bil</span>
          <button type="button" className={css.sheetClose} onClick={onClose}>
            Stäng ✕
          </button>
        </div>

        <div className={css.panelBody}>
          <span className={css.panelLabel}>Sök med registreringsnummer</span>
          {/* page="carSwitch": a lookup hands the car to updateState instead of
            * opening the product picker — the product is chosen in the bar. */}
          <RegNumInput
            page="carSwitch"
            listingType="wheel"
            size="inline"
            submitLabel="Kolla"
            savedRegNum={regNum}
            changeRegNum={onRegNumChange}
            updateState={payload => onPlateCar(payload, normalizeReg(regNum))}
          />
          {seriesHint ? (
            <p className={css.panelHint} role="status">
              {formatReg(seriesHint)}: välj motor — skriv in den ovan och tryck Kolla.
            </p>
          ) : null}

          <span className={css.panelLabel}>Sök på din bilmodell</span>
          <CarModelPicker idPrefix="carBarModel" onResolved={payload => onModelCar(payload)} />

          {chips.length > 0 ? (
            <>
              <div className={css.historyHeader}>
                <span className={css.historyTitle}>Tidigare sökningar</span>
                {chips.some(chip => chip.removable) ? (
                  <button type="button" className={css.historyClear} onClick={onClearHistory}>
                    Rensa historik
                  </button>
                ) : null}
              </div>
              <div className={css.historyChips} role="list" aria-label="Tidigare sökningar">
                {chips.map(chip => {
                  const active = chip.key === activeKey;
                  return (
                    <span
                      key={chip.key}
                      role="listitem"
                      className={classNames(css.historyChip, {
                        [css.historyChipActive]: active,
                      })}
                    >
                      <button
                        type="button"
                        className={css.historyPick}
                        onClick={() => onPickChip(chip)}
                        disabled={!!pickingKey}
                        aria-current={active ? 'true' : undefined}
                      >
                        {chip.reg ? (
                          <strong className={css.historyReg}>{formatReg(chip.reg)}</strong>
                        ) : null}
                        {chip.reg && chip.label ? ' · ' : null}
                        {chip.label}
                        {chip.saved ? <span className={css.historySaved}> · Sparad</span> : null}
                      </button>
                      {chip.removable ? (
                        <button
                          type="button"
                          className={css.historyRemove}
                          onClick={() => onRemoveChip(chip)}
                          aria-label={`Ta bort ${chip.reg ? formatReg(chip.reg) : chip.label}`}
                        >
                          ✕
                        </button>
                      ) : null}
                    </span>
                  );
                })}
              </div>
            </>
          ) : null}

          {hasCar ? (
            <button type="button" className={css.withoutCar} onClick={onSearchWithoutCar}>
              Sök utan bil
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

CarSwitchPanel.defaultProps = {
  activeKey: null,
  pickingKey: null,
  seriesHint: null,
  hasCar: false,
};

CarSwitchPanel.propTypes = {
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  activeKey: string,
  pickingKey: string,
  onPickChip: func.isRequired,
  onRemoveChip: func.isRequired,
  onClearHistory: func.isRequired,
  onPlateCar: func.isRequired,
  onModelCar: func.isRequired,
  onSearchWithoutCar: func.isRequired,
  hasCar: bool,
  seriesHint: string,
  regNum: string.isRequired,
  onRegNumChange: func.isRequired,
};

export default CarSwitchPanel;
